import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, CheckSquare, Edit3, AlertCircle, Calendar, Users, HelpCircle } from 'lucide-react';
import { Product, Feature, ProductMarketFitQA, DEFAULT_INDUSTRY_AREAS, BoardMember, Task, Resource, ResourceAllocation, FULL_TIME_HOURS_PER_MONTH, ProductPricingModel, PRODUCT_PRICING_MODEL_OPTIONS, getProductPricingModelLabel } from '../types';
import { calculatePriorityScore } from '../utils/priorityScore';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => void;
  /** Partial updates for saved products (e.g. market-fit Q&A) without closing the modal. */
  onPatchProduct?: (productId: string, updates: Partial<Product>) => Promise<void>;
  onDelete?: (productId: string) => void;
  product?: Product;
  columnId: string;
  boardId: string;
  userId: string;
  boardMembers?: BoardMember[];
  resources?: Resource[];
  allocations?: ResourceAllocation[];
}

const ProductModal: React.FC<ProductModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onPatchProduct,
  onDelete,
  product,
  columnId,
  boardId,
  userId,
  boardMembers,
  resources = [],
  allocations = [],
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [effortLevel, setEffortLevel] = useState<'Low' | 'Medium' | 'High'>('Medium');
  const [marketPotential, setMarketPotential] = useState<'Low' | 'Medium' | 'High'>('Medium');
  const [gtmReadyPercent, setGtmReadyPercent] = useState<number>(0);
  const [status, setStatus] = useState<'Ideas' | 'Research' | 'Development' | 'Testing' | 'Launch'>('Ideas');
  const [pricingModel, setPricingModel] = useState<ProductPricingModel>('time_and_materials');
  const [features, setFeatures] = useState<Feature[]>([]);
  const [competitors, setCompetitors] = useState<string[]>(['']);
  const [marketCrowding, setMarketCrowding] = useState<'No Competition' | 'Low Competition' | 'Medium Competition' | 'High Competition'>('Medium Competition');
  const [marketType, setMarketType] = useState<'Blue Ocean' | 'Red Ocean'>('Red Ocean');
  const [businessModel, setBusinessModel] = useState<'SaaS' | 'Marketplace' | 'E-commerce' | 'Mobile App' | 'API/Platform' | 'Hardware' | 'Consulting' | 'Other'>('SaaS');
  const [supportOverhead, setSupportOverhead] = useState<'Low' | 'Medium' | 'High'>('Medium');
  const [industryAreas, setIndustryAreas] = useState<string[]>([]);
  const [links, setLinks] = useState<Array<{
    id: string;
    title: string;
    url: string;
    type: 'design' | 'documentation' | 'research' | 'competitor' | 'other';
  }>>([]);
  const [owner, setOwner] = useState<string>('');
  const [aiSummary, setAiSummary] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Task-related state
  const { user } = useAuth();
  const { addNotification } = useNotifications();

  // Get resource allocations for this product
  const productAllocations = product ? allocations.filter(allocation => allocation.productId === product.id) : [];
  const hoursFromAllocation = (a: ResourceAllocation) => a.hoursPerMonth ?? (a.percent * FULL_TIME_HOURS_PER_MONTH / 100);
  const totalAllocationHours = productAllocations.reduce((sum, allocation) => sum + hoursFromAllocation(allocation), 0);

  // Get resource details for allocations
  const resourceAllocations = productAllocations.map(allocation => {
    const resource = resources.find(r => r.id === allocation.resourceId);
    return {
      ...allocation,
      resourceName: resource?.name || 'Unknown',
      resourceRole: resource?.role || 'Unknown'
    };
  });
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [editingTask, setEditingTask] = useState<string | null>(null);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    status: 'Not Started' as const,
    priority: 'Medium' as const,
    owner: '',
    link: '',
    dueDate: ''
  });

  const [marketFitQa, setMarketFitQa] = useState<ProductMarketFitQA[]>([]);
  const [isAddingQa, setIsAddingQa] = useState(false);
  const [newQaQuestion, setNewQaQuestion] = useState('');
  const [newQaAnswer, setNewQaAnswer] = useState('');
  const [editingQaId, setEditingQaId] = useState<string | null>(null);
  const [editQaQuestion, setEditQaQuestion] = useState('');
  const [editQaAnswer, setEditQaAnswer] = useState('');

  const resolveQaAuthorLabel = (uid: string, email?: string) => {
    const m = boardMembers?.find((b) => b.userId === uid);
    return m?.name || m?.email || email || 'Teammate';
  };

  const pushMarketFitQaRemote = async (next: ProductMarketFitQA[]) => {
    if (product?.id && onPatchProduct) {
      await onPatchProduct(product.id, { marketFitQa: next });
    }
  };

  const handleAddMarketFitQa = async () => {
    if (!newQaQuestion.trim()) return;
    const prev = marketFitQa;
    const entry: ProductMarketFitQA = {
      id: `qa_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      question: newQaQuestion.trim(),
      answer: newQaAnswer.trim() || undefined,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: user?.id || userId,
      createdByEmail: user?.email ?? undefined,
    };
    const next = [...marketFitQa, entry];
    setMarketFitQa(next);
    setNewQaQuestion('');
    setNewQaAnswer('');
    setIsAddingQa(false);
    try {
      await pushMarketFitQaRemote(next);
    } catch {
      setMarketFitQa(prev);
    }
  };

  const handleDeleteMarketFitQa = async (qaId: string) => {
    if (!window.confirm('Remove this Q&A entry?')) return;
    const prev = marketFitQa;
    const next = marketFitQa.filter((q) => q.id !== qaId);
    setMarketFitQa(next);
    try {
      await pushMarketFitQaRemote(next);
    } catch {
      setMarketFitQa(prev);
    }
  };

  const handleSaveEditMarketFitQa = async () => {
    if (!editingQaId || !editQaQuestion.trim()) return;
    const prev = marketFitQa;
    const next = marketFitQa.map((q) =>
      q.id === editingQaId
        ? {
            ...q,
            question: editQaQuestion.trim(),
            answer: editQaAnswer.trim() || undefined,
            updatedAt: new Date(),
          }
        : q
    );
    setMarketFitQa(next);
    setEditingQaId(null);
    try {
      await pushMarketFitQaRemote(next);
    } catch {
      setMarketFitQa(prev);
    }
  };

  // Task management functions
  const loadTasks = async () => {
    if (!product) return;
    
    try {
      // TODO: Implement getTasksForProduct in firestore
      // For now, using mock data
      const mockTasks: Task[] = [
        {
          id: '1',
          productId: product.id,
          boardId: boardId,
          title: 'Market Research',
          description: 'Conduct competitor analysis',
          status: 'In Progress',
          priority: 'High',
          owner: user?.id || '',
          ownerEmail: user?.email || '',
          createdBy: user?.id || '',
          createdByEmail: user?.email || '',
          createdAt: new Date(),
          updatedAt: new Date(),
          dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        }
      ];
      setTasks(mockTasks);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim() || !user || !product) return;

    try {
      const taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'> = {
        productId: product.id,
        boardId: boardId,
        title: newTask.title.trim(),
        description: newTask.description.trim(),
        status: newTask.status,
        priority: newTask.priority,
        owner: newTask.owner || undefined,
        ownerEmail: boardMembers?.find(m => m.userId === newTask.owner)?.email,
        link: newTask.link || undefined,
        createdBy: user.id,
        createdByEmail: user.email,
        dueDate: newTask.dueDate ? new Date(newTask.dueDate) : undefined
      };

      // TODO: Implement addTask in firestore
      const newTaskObj: Task = {
        ...taskData,
        id: Date.now().toString(),
        createdAt: new Date(),
        updatedAt: new Date()
      };

      setTasks([...tasks, newTaskObj]);
      
      // Send notification to task owner if assigned
      if (newTask.owner && newTask.owner !== user.id) {
        addNotification({
          userId: newTask.owner,
          type: 'board_update',
          title: 'New task assigned',
          message: `${user.email} assigned you a task: ${newTask.title}`,
          boardId: boardId,
          productId: product.id,
          createdBy: user.id,
          createdByEmail: user.email,
          isRead: false,
        });
      }

      setNewTask({
        title: '',
        description: '',
        status: 'Not Started',
        priority: 'Medium',
        owner: '',
        link: '',
        dueDate: ''
      });
      setIsAddingTask(false);
    } catch (error) {
      console.error('Error adding task:', error);
    }
  };

  const handleUpdateTask = async (taskId: string, updates: Partial<Task>) => {
    try {
      // TODO: Implement updateTask in firestore
      setTasks(tasks.map(task => 
        task.id === taskId 
          ? { ...task, ...updates, updatedAt: new Date() }
          : task
      ));
      setEditingTask(null);
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      // TODO: Implement deleteTask in firestore
      setTasks(tasks.filter(task => task.id !== taskId));
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed':
        return <CheckSquare size={14} className="text-green-600" />;
      case 'In Progress':
        return <AlertCircle size={14} className="text-blue-600" />;
      case 'Blocked':
        return <AlertCircle size={14} className="text-red-600" />;
      case 'On Hold':
        return <AlertCircle size={14} className="text-yellow-600" />;
      default:
        return <CheckSquare size={14} className="text-gray-400" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High':
        return 'text-red-600 bg-red-100';
      case 'Medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'Low':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString();
  };

  const isOverdue = (dueDate: Date) => {
    return new Date(dueDate) < new Date();
  };

  // Load product data when editing
  useEffect(() => {
    if (product) {
      // Load tasks for the product
      loadTasks();
      
      // Load product data
      setName(product.name);
      setDescription(product.description || '');
      setAiSummary(product.aiSummary || '');
      setEffortLevel(product.effortLevel);
      setMarketPotential(product.marketPotential);
      setGtmReadyPercent(product.gtmReadyPercent || 0);
      setStatus(product.status || 'Ideas');
      setPricingModel(product.pricingModel ?? 'time_and_materials');
      setFeatures(product.features.length > 0 ? product.features : []);
      setCompetitors(product.competitors && product.competitors.length > 0 ? product.competitors : ['']);
      setMarketCrowding(product.marketCrowding || 'Medium Competition');
      setMarketType(product.marketType || 'Red Ocean');
      setBusinessModel(product.businessModel || 'SaaS');
      setSupportOverhead(product.supportOverhead || 'Medium');
      setIndustryAreas(product.industryAreas || []);
      setLinks(product.links || []);
      setOwner(product.owner || '');
      setMarketFitQa(product.marketFitQa ?? []);
      setIsAddingQa(false);
      setEditingQaId(null);
      setNewQaQuestion('');
      setNewQaAnswer('');
      setEditQaQuestion('');
      setEditQaAnswer('');
      
      // Reset to read-only mode when opening existing product
      setIsEditMode(false);
    } else {
      // Reset form for new product
      setName('');
      setDescription('');
      setAiSummary('');
      setEffortLevel('Medium');
      setMarketPotential('Medium');
      setGtmReadyPercent(0);
      setStatus('Ideas');
      setPricingModel('time_and_materials');
      setFeatures([]);
      setCompetitors(['']);
      setMarketCrowding('Medium Competition');
      setMarketType('Red Ocean');
      setBusinessModel('SaaS');
      setSupportOverhead('Medium');
      setIndustryAreas([]);
      setLinks([]);
      setOwner('');
      setMarketFitQa([]);
      setIsAddingQa(false);
      setEditingQaId(null);
      setNewQaQuestion('');
      setNewQaAnswer('');
      setEditQaQuestion('');
      setEditQaAnswer('');
      
      // New products start in edit mode
      setIsEditMode(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadTasks intentionally omitted to avoid re-run on every loadTasks ref
  }, [product, isOpen]);

  // Add escape key functionality
  useEscapeKey(onClose, isOpen);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) return;

    setIsSubmitting(true);
    
    try {
      const productData: any = {
        name: name.trim(),
        effortLevel,
        marketPotential,
        gtmReadyPercent,
        status,
        pricingModel,
        features: features || [],
        competitors: competitors.filter(c => c && c.trim() !== ''),
        marketCrowding,
        marketType,
        businessModel,
        supportOverhead,
        industryAreas: industryAreas || [],
        links: links || [],
        owner: owner.trim() || undefined,
        columnId,
        boardId,
        createdBy: userId,
      };

      if (description.trim()) {
        productData.description = description.trim();
      }
      if (aiSummary.trim()) {
        productData.aiSummary = aiSummary.trim();
      }
      productData.marketFitQa = marketFitQa;

      // Calculate priority score
      const priorityScore = calculatePriorityScore(productData as Product);
      productData.priorityScore = priorityScore;

      await onSave(productData);
      onClose();
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addFeature = () => {
    const newFeature: Feature = {
      id: `feature_${Date.now()}`,
      name: '',
      status: 'Not Started',
      description: '',
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setFeatures([...features, newFeature]);
  };

  const removeFeature = (index: number) => {
    if (features && features.length > 1) {
      setFeatures(features.filter((_, i) => i !== index));
    }
  };

  const updateFeature = (index: number, value: string) => {
    const newFeatures = [...features];
    newFeatures[index] = { ...newFeatures[index], name: value, updatedAt: new Date() };
    setFeatures(newFeatures);
  };

  const addCompetitor = () => {
    setCompetitors([...competitors, '']);
  };

  const removeCompetitor = (index: number) => {
    if (competitors && competitors.length > 1) {
      setCompetitors(competitors.filter((_, i) => i !== index));
    }
  };

  const handleDelete = async () => {
    if (!product || !onDelete) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete "${product.name}"? This action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    setIsDeleting(true);
    try {
      await onDelete(product.id);
      onClose();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const updateCompetitor = (index: number, value: string) => {
    const newCompetitors = [...competitors];
    newCompetitors[index] = value;
    setCompetitors(newCompetitors);
  };

  const addLink = () => {
    const newLink = {
      id: `link_${Date.now()}`,
      title: '',
      url: '',
      type: 'other' as const
    };
    setLinks([...links, newLink]);
  };

  const removeLink = (index: number) => {
    if (links && links.length > 1) {
      setLinks(links.filter((_, i) => i !== index));
    }
  };

  const updateLink = (index: number, field: 'title' | 'url' | 'type', value: string) => {
    const newLinks = [...links];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setLinks(newLinks);
  };

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white/15 backdrop-blur-xl z-[9999] overflow-hidden"
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="h-full w-full flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white/80 backdrop-blur-sm">
                <h2 className="text-2xl font-bold text-gray-800">
                  {product ? (isEditMode ? 'Edit Product' : product.name) : 'Add New Product'}
                </h2>
                <div className="flex items-center space-x-3">
                  {product && !isEditMode && (
                    <button
                      onClick={() => setIsEditMode(true)}
                      className="btn-primary text-sm py-2 px-4 flex items-center space-x-2"
                    >
                      <Edit3 size={16} />
                      <span>Edit</span>
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              <div className="flex-1 flex overflow-hidden">
                {/* Left side - Product Form */}
                <div className="flex-1 overflow-y-auto">
                  {isEditMode ? (
                    <form 
                      id="product-form" 
                      onSubmit={handleSubmit} 
                      className="p-6 space-y-6"
                      onScroll={(e) => e.stopPropagation()}
                    >
                    {/* Product Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Product Name *
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="input-field w-full"
                        placeholder="Enter product name"
                        required
                      />
                    </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Description
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="input-field w-full resize-none"
                      rows={3}
                      placeholder="Describe your product idea in detail..."
                    />
                  </div>

                  {/* Owner */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Owner
                    </label>
                    <select
                      value={owner}
                      onChange={(e) => setOwner(e.target.value)}
                      className="input-field w-full"
                    >
                      <option value="">Select Owner</option>
                                           {boardMembers?.map(member => (
                         <option key={member.userId} value={member.name || member.email}>{member.name || member.email}</option>
                       ))}
                    </select>
                  </div>

                  {/* Resource Allocations */}
                  {resourceAllocations.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Users className="h-4 w-4 inline mr-1" />
                        Resource Allocations ({totalAllocationHours} h)
                      </label>
                      <div className="space-y-2">
                        {resourceAllocations.map((allocation) => (
                          <div key={allocation.id} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div>
                              <div className="font-medium text-blue-900">{allocation.resourceName}</div>
                              <div className="text-sm text-blue-700">{allocation.resourceRole}</div>
                            </div>
                            <div className="text-lg font-semibold text-blue-900">
                              {hoursFromAllocation(allocation)} h/mo
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Links */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Links
                    </label>
                    <div className="space-y-3">
                      {links.map((link, index) => (
                        <div key={link.id} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                            <input
                              type="text"
                              value={link.title}
                              onChange={(e) => updateLink(index, 'title', e.target.value)}
                              className="input-field"
                              placeholder="Link title"
                            />
                            <input
                              type="url"
                              value={link.url}
                              onChange={(e) => updateLink(index, 'url', e.target.value)}
                              className="input-field"
                              placeholder="https://..."
                            />
                            <select
                              value={link.type}
                              onChange={(e) => updateLink(index, 'type', e.target.value)}
                              className="input-field"
                            >
                              <option value="design">Design</option>
                              <option value="documentation">Documentation</option>
                              <option value="research">Research</option>
                              <option value="competitor">Competitor</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          {links.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLink(index)}
                              className="text-red-500 hover:text-red-700 transition-colors p-2"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addLink}
                        className="btn-secondary text-sm py-2 px-3 flex items-center space-x-2"
                      >
                        <Plus size={16} />
                        <span>Add Link</span>
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Add relevant links to designs, documentation, research, or competitor analysis
                    </p>
                  </div>

                  {/* AI Summary */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      AI Summary (for card view)
                    </label>
                    <textarea
                      value={aiSummary}
                      onChange={(e) => setAiSummary(e.target.value)}
                      className="input-field w-full resize-none"
                      rows={2}
                      placeholder="AI-generated concise summary for card display..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Brief summary that appears on the card (auto-generated when saved)
                    </p>
                  </div>

                  {/* Priority Score Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">Priority Score Calculation</h4>
                    <div className="text-xs text-blue-700 space-y-1">
                      <div>• Market Potential: 0-25 points (High = 25, Medium = 15, Low = 5)</div>
                      <div>• GTM Ready %: 0-25 points (higher % = higher priority)</div>
                      <div>• Status: 0-20 points (Launch = 20, Testing = 18, Development = 15, etc.)</div>
                      <div>• Effort Level: 0-15 points (Low = 15, Medium = 10, High = 5)</div>
                      <div>• Market Crowding: 0-8 points (No Competition = 8, Low = 6, Medium = 4, High = 2)</div>
                      <div>• Market Type: 0-7 points (Blue Ocean = 7, Red Ocean = 3)</div>
                      <div>• Business Model: 0-5 points (SaaS/API = 5, Marketplace/App = 4, etc.)</div>
                      <div>• Support Overhead: 0-5 points (Low = 5, Medium = 3, High = 1)</div>
                      <div>• Features: 0-10 points (2 points per feature, max 10)</div>
                      <div>• Competitors: 0-3 points (if identified)</div>
                      <div>• Age: 0-7 points (older products get slight boost)</div>
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value as 'Ideas' | 'Research' | 'Development' | 'Testing' | 'Launch')}
                        className="input-field w-full"
                      >
                        <option value="Ideas">Ideas</option>
                        <option value="Research">Research</option>
                        <option value="Development">Development</option>
                        <option value="Testing">Testing</option>
                        <option value="Launch">Launch</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Project pricing
                      </label>
                      <select
                        value={pricingModel}
                        onChange={(e) => setPricingModel(e.target.value as ProductPricingModel)}
                        className="input-field w-full"
                      >
                        {PRODUCT_PRICING_MODEL_OPTIONS.map(({ value, label }) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Time &amp; materials, fixed price, Internal, or Product
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Go-to-Market Effort
                      </label>
                      <select
                        value={effortLevel}
                        onChange={(e) => setEffortLevel(e.target.value as 'Low' | 'Medium' | 'High')}
                        className="input-field w-full"
                      >
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Market Potential
                      </label>
                      <select
                        value={marketPotential}
                        onChange={(e) => setMarketPotential(e.target.value as 'Low' | 'Medium' | 'High')}
                        className="input-field w-full"
                      >
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        GTM Ready (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={gtmReadyPercent}
                        onChange={(e) => setGtmReadyPercent(parseInt(e.target.value) || 0)}
                        className="input-field w-full"
                        placeholder="0-100"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        How ready for first customer
                      </p>
                    </div>
                  </div>

                  {/* Additional Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Market Crowding
                      </label>
                      <select
                        value={marketCrowding}
                        onChange={(e) => setMarketCrowding(e.target.value as 'No Competition' | 'Low Competition' | 'Medium Competition' | 'High Competition')}
                        className="input-field w-full"
                      >
                        <option value="No Competition">No Competition</option>
                        <option value="Low Competition">Low Competition</option>
                        <option value="Medium Competition">Medium Competition</option>
                        <option value="High Competition">High Competition</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Market Type
                      </label>
                      <select
                        value={marketType}
                        onChange={(e) => setMarketType(e.target.value as 'Blue Ocean' | 'Red Ocean')}
                        className="input-field w-full"
                      >
                        <option value="Blue Ocean">Blue Ocean</option>
                        <option value="Red Ocean">Red Ocean</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Blue Ocean = untapped market, Red Ocean = competitive market
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Business Model
                      </label>
                      <select
                        value={businessModel}
                        onChange={(e) => setBusinessModel(e.target.value as 'SaaS' | 'Marketplace' | 'E-commerce' | 'Mobile App' | 'API/Platform' | 'Hardware' | 'Consulting' | 'Other')}
                        className="input-field w-full"
                      >
                        <option value="SaaS">SaaS</option>
                        <option value="API/Platform">API/Platform</option>
                        <option value="Marketplace">Marketplace</option>
                        <option value="Mobile App">Mobile App</option>
                        <option value="E-commerce">E-commerce</option>
                        <option value="Hardware">Hardware</option>
                        <option value="Consulting">Consulting</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Support Overhead
                      </label>
                      <select
                        value={supportOverhead}
                        onChange={(e) => setSupportOverhead(e.target.value as 'Low' | 'Medium' | 'High')}
                        className="input-field w-full"
                      >
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Difficulty of customer support
                      </p>
                    </div>
                  </div>

                  {/* Industry Areas - Full Width */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Industry Areas
                    </label>
                    <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-xl p-3 bg-white/15">
                      <div className="grid grid-cols-3 gap-3">
                        {DEFAULT_INDUSTRY_AREAS.map((industry) => (
                          <label key={industry} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={industryAreas.includes(industry)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setIndustryAreas([...industryAreas, industry]);
                                } else {
                                  setIndustryAreas(industryAreas.filter(area => area !== industry));
                                }
                              }}
                              className="rounded border-gray-300 text-satsuma-600 focus:ring-satsuma-500"
                            />
                            <span className="text-sm text-gray-700">{industry}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Select all relevant industries this product will affect
                    </p>
                  </div>

                  {/* Features */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Features
                    </label>
                    <div className="space-y-2">
                      {features.map((feature, index) => (
                        <div key={feature.id} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={feature.name}
                            onChange={(e) => updateFeature(index, e.target.value)}
                            className="input-field flex-1"
                            placeholder="Enter a feature"
                          />
                          {features.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeFeature(index)}
                              className="text-red-500 hover:text-red-700 transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addFeature}
                        className="btn-secondary text-sm py-2 px-3 flex items-center space-x-2"
                      >
                        <Plus size={16} />
                        <span>Add Feature</span>
                      </button>
                    </div>
                  </div>

                  {/* Competitors */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Competitors
                      </label>
                    </div>
                    <div className="space-y-2">
                      {competitors.map((competitor, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={competitor}
                            onChange={(e) => updateCompetitor(index, e.target.value)}
                            className="input-field flex-1"
                            placeholder="Enter a competitor"
                          />
                          {competitors.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeCompetitor(index)}
                              className="text-red-500 hover:text-red-700 transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addCompetitor}
                        className="btn-secondary text-sm py-2 px-3 flex items-center space-x-2"
                      >
                        <Plus size={16} />
                        <span>Add Competitor</span>
                      </button>
                    </div>
                  </div>

                  {/* Market fit Q&A (new product — saved with Create Product) */}
                  {!product && (
                    <div className="border-t border-gray-200 pt-6 mt-6">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                          <HelpCircle size={18} className="text-satsuma-500 shrink-0" />
                          Market fit Q&A
                        </label>
                        <button
                          type="button"
                          onClick={() => setIsAddingQa(true)}
                          className="text-satsuma-500 hover:text-satsuma-600 transition-colors"
                          title="Add Q&A"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        Log questions and answers from discovery sessions; they are stored on this product.
                      </p>
                      {marketFitQa.length === 0 && !isAddingQa ? (
                        <p className="text-xs text-gray-400 text-center py-4">No entries yet</p>
                      ) : (
                        <div className="space-y-2">
                          {marketFitQa.map((qa) => (
                            <div key={qa.id} className="p-3 bg-white border border-gray-200 rounded-lg text-sm">
                              {editingQaId === qa.id ? (
                                <div className="space-y-2">
                                  <textarea
                                    value={editQaQuestion}
                                    onChange={(e) => setEditQaQuestion(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded text-xs"
                                    rows={2}
                                    placeholder="Question"
                                  />
                                  <textarea
                                    value={editQaAnswer}
                                    onChange={(e) => setEditQaAnswer(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded text-xs"
                                    rows={2}
                                    placeholder="Answer (optional)"
                                  />
                                  <div className="flex gap-1">
                                    <button
                                      type="button"
                                      onClick={() => void handleSaveEditMarketFitQa()}
                                      className="flex-1 px-2 py-1 text-xs bg-satsuma-500 text-white rounded hover:bg-satsuma-600"
                                    >
                                      Save
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setEditingQaId(null)}
                                      className="px-2 py-1 text-xs text-gray-600"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="flex justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <div className="font-medium text-gray-800 text-xs">Q: {qa.question}</div>
                                      {qa.answer ? (
                                        <div className="text-gray-600 text-xs mt-1 whitespace-pre-wrap">A: {qa.answer}</div>
                                      ) : null}
                                      <div className="text-[10px] text-gray-400 mt-1">
                                        {resolveQaAuthorLabel(qa.createdBy, qa.createdByEmail)} ·{' '}
                                        {qa.createdAt ? new Date(qa.createdAt).toLocaleDateString() : ''}
                                      </div>
                                    </div>
                                    <div className="flex items-start gap-1 shrink-0">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditingQaId(qa.id);
                                          setEditQaQuestion(qa.question);
                                          setEditQaAnswer(qa.answer || '');
                                        }}
                                        className="text-gray-400 hover:text-gray-600"
                                      >
                                        <Edit3 size={12} />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => void handleDeleteMarketFitQa(qa.id)}
                                        className="text-gray-400 hover:text-red-600"
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {isAddingQa && (
                        <div className="mt-3 p-3 bg-white border border-gray-200 rounded-lg space-y-2">
                          <textarea
                            value={newQaQuestion}
                            onChange={(e) => setNewQaQuestion(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded text-xs"
                            rows={2}
                            placeholder="Question"
                          />
                          <textarea
                            value={newQaAnswer}
                            onChange={(e) => setNewQaAnswer(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded text-xs"
                            rows={2}
                            placeholder="Answer (optional)"
                          />
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => void handleAddMarketFitQa()}
                              disabled={!newQaQuestion.trim()}
                              className="flex-1 bg-satsuma-500 text-white py-1 px-2 rounded text-xs font-medium hover:bg-satsuma-600 disabled:opacity-50"
                            >
                              Add
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setIsAddingQa(false);
                                setNewQaQuestion('');
                                setNewQaAnswer('');
                              }}
                              className="px-2 py-1 text-xs text-gray-600"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                    </form>
                  ) : (
                    /* Read-only view */
                    <div className="p-6 space-y-6">
                      {/* Product Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Product Name
                        </label>
                        <div className="text-gray-900 font-medium">{name || 'No name provided'}</div>
                      </div>

                      {/* Description */}
                      {description && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Product Description
                          </label>
                          <div className="text-gray-900 whitespace-pre-wrap">{description}</div>
                        </div>
                      )}

                      {/* Owner */}
                      {owner && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Owner
                          </label>
                          <div className="text-gray-900">{owner}</div>
                        </div>
                      )}

                      {/* Resource Allocations */}
                      {resourceAllocations.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Users className="h-4 w-4 inline mr-1" />
                            Resource Allocations ({totalAllocationHours} h)
                          </label>
                          <div className="space-y-2">
                            {resourceAllocations.map((allocation) => (
                              <div key={allocation.id} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <div>
                                  <div className="font-medium text-blue-900">{allocation.resourceName}</div>
                                  <div className="text-sm text-blue-700">{allocation.resourceRole}</div>
                                </div>
                                <div className="text-lg font-semibold text-blue-900">
                                  {hoursFromAllocation(allocation)} h/mo
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Links */}
                      {links && links.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Links
                          </label>
                          <div className="space-y-2">
                            {links.map((link) => (
                              <div key={link.id} className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                  <div>
                                    <span className="font-medium text-gray-700">Title:</span>
                                    <div className="text-gray-900">{link.title || 'No title'}</div>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-700">URL:</span>
                                    <div className="text-gray-900">
                                      {link.url ? (
                                        <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                                          {link.url}
                                        </a>
                                      ) : (
                                        'No URL'
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-700">Type:</span>
                                    <div className="text-gray-900 capitalize">{link.type}</div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* AI Summary */}
                      {aiSummary && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            AI Summary
                          </label>
                          <div className="text-gray-900">{aiSummary}</div>
                        </div>
                      )}

                      {/* Metrics */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status
                          </label>
                          <div className="text-gray-900">{status}</div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Project pricing
                          </label>
                          <div className="text-gray-900">{getProductPricingModelLabel(pricingModel)}</div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Go-to-Market Effort
                          </label>
                          <div className="text-gray-900">{effortLevel}</div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Market Potential
                          </label>
                          <div className="text-gray-900">{marketPotential}</div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            GTM Ready (%)
                          </label>
                          <div className="text-gray-900">{gtmReadyPercent}%</div>
                        </div>
                      </div>

                      {/* Additional Metrics */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Market Crowding
                          </label>
                          <div className="text-gray-900">{marketCrowding}</div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Market Type
                          </label>
                          <div className="text-gray-900">{marketType}</div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Business Model
                          </label>
                          <div className="text-gray-900">{businessModel}</div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Support Overhead
                          </label>
                          <div className="text-gray-900">{supportOverhead}</div>
                        </div>
                      </div>

                      {/* Industry Areas */}
                      {industryAreas && industryAreas.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Industry Areas
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {industryAreas.map((industry) => (
                              <span key={industry} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                                {industry}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Features */}
                      {features && features.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Features
                          </label>
                          <div className="space-y-1">
                            {features.map((feature) => (
                              <div key={feature.id} className="text-gray-900">
                                • {feature.name}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Competitors */}
                      {competitors && competitors.length > 0 && competitors.some(c => c.trim()) && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Competitors
                          </label>
                          <div className="space-y-1">
                            {competitors.filter(c => c.trim()).map((competitor, index) => (
                              <div key={index} className="text-gray-900">
                                • {competitor}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Right side - Tasks */}
                {product && boardMembers && (
                  <div className="w-96 border-l border-gray-200 bg-gray-50 overflow-y-auto">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-800">Tasks</h3>
                        <button
                          onClick={() => setIsAddingTask(true)}
                          className="text-satsuma-500 hover:text-satsuma-600 transition-colors"
                        >
                          <Plus size={16} />
                        </button>
                      </div>

                      {/* Task List */}
                      <div className="space-y-3">
                        {tasks.length === 0 ? (
                          <div className="text-center py-8">
                            <CheckSquare size={24} className="text-gray-300 mx-auto mb-2" />
                            <p className="text-xs text-gray-500">No tasks yet</p>
                          </div>
                        ) : (
                          tasks.map((task) => (
                            <div key={task.id} className="bg-white rounded p-3 border border-gray-200">
                              {editingTask === task.id ? (
                                <div className="space-y-2">
                                  <input
                                    type="text"
                                    value={task.title}
                                    onChange={(e) => handleUpdateTask(task.id, { title: e.target.value })}
                                    className="w-full p-1 border border-gray-300 rounded text-xs"
                                    placeholder="Task title"
                                  />
                                  <div className="flex space-x-1">
                                    <select
                                      value={task.status}
                                      onChange={(e) => handleUpdateTask(task.id, { status: e.target.value as any })}
                                      className="flex-1 p-1 border border-gray-300 rounded text-xs"
                                    >
                                      <option value="Not Started">Not Started</option>
                                      <option value="In Progress">In Progress</option>
                                      <option value="Completed">Completed</option>
                                      <option value="Blocked">Blocked</option>
                                      <option value="On Hold">On Hold</option>
                                    </select>
                                    <select
                                      value={task.priority}
                                      onChange={(e) => handleUpdateTask(task.id, { priority: e.target.value as any })}
                                      className="flex-1 p-1 border border-gray-300 rounded text-xs"
                                    >
                                      <option value="Low">Low</option>
                                      <option value="Medium">Medium</option>
                                      <option value="High">High</option>
                                    </select>
                                  </div>
                                  <div className="flex space-x-1">
                                    <button
                                      onClick={() => setEditingTask(null)}
                                      className="flex-1 px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setEditingTask(null)}
                                      className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div className="flex items-start justify-between mb-1">
                                    <div className="flex items-center space-x-1 flex-1">
                                      {getStatusIcon(task.status)}
                                      <h5 className="text-xs font-medium text-gray-800 flex-1 truncate">
                                        {task.title}
                                      </h5>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                      <button
                                        onClick={() => setEditingTask(task.id)}
                                        className="text-gray-400 hover:text-gray-600 transition-colors"
                                      >
                                        <Edit3 size={10} />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteTask(task.id)}
                                        className="text-gray-400 hover:text-red-600 transition-colors"
                                      >
                                        <Trash2 size={10} />
                                      </button>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center justify-between">
                                    <span className={`inline-flex items-center px-1 py-0.5 rounded text-xs font-medium ${getPriorityColor(task.priority)}`}>
                                      {task.priority}
                                    </span>
                                    {task.dueDate && (
                                      <div className={`flex items-center space-x-1 text-xs ${isOverdue(task.dueDate) ? 'text-red-500' : 'text-gray-500'}`}>
                                        <Calendar size={10} />
                                        <span>{formatDate(task.dueDate)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>

                      {/* Add Task Form */}
                      {isAddingTask && (
                        <div className="mt-4 p-3 bg-white rounded border border-gray-200">
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={newTask.title}
                              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                              className="w-full p-1 border border-gray-300 rounded text-xs"
                              placeholder="Task title"
                            />
                            <div className="flex space-x-1">
                              <select
                                value={newTask.status}
                                onChange={(e) => setNewTask({ ...newTask, status: e.target.value as any })}
                                className="flex-1 p-1 border border-gray-300 rounded text-xs"
                              >
                                <option value="Not Started">Not Started</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Completed">Completed</option>
                                <option value="Blocked">Blocked</option>
                                <option value="On Hold">On Hold</option>
                              </select>
                              <select
                                value={newTask.priority}
                                onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as any })}
                                className="flex-1 p-1 border border-gray-300 rounded text-xs"
                              >
                                <option value="Low">Low</option>
                                <option value="Medium">Medium</option>
                                <option value="High">High</option>
                              </select>
                            </div>
                            <div className="flex space-x-1">
                              <select
                                value={newTask.owner}
                                onChange={(e) => setNewTask({ ...newTask, owner: e.target.value })}
                                className="flex-1 p-1 border border-gray-300 rounded text-xs"
                              >
                                <option value="">Unassigned</option>
                                {boardMembers.map(member => (
                                  <option key={member.userId} value={member.userId}>
                                    {member.name || member.email}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="date"
                                value={newTask.dueDate}
                                onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                                className="flex-1 p-1 border border-gray-300 rounded text-xs"
                              />
                            </div>
                            <div className="flex space-x-1">
                              <button
                                onClick={handleAddTask}
                                disabled={!newTask.title.trim()}
                                className="flex-1 bg-satsuma-500 text-white py-1 px-2 rounded text-xs font-medium hover:bg-satsuma-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Add
                              </button>
                              <button
                                onClick={() => setIsAddingTask(false)}
                                className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Market fit Q&A — under tasks */}
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                            <HelpCircle size={16} className="text-satsuma-500 shrink-0" />
                            Market fit Q&A
                          </h3>
                          <button
                            type="button"
                            onClick={() => setIsAddingQa(true)}
                            className="text-satsuma-500 hover:text-satsuma-600 transition-colors"
                            title="Add Q&A"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mb-3">
                          Log questions and answers from discovery sessions.
                        </p>
                        {marketFitQa.length === 0 && !isAddingQa ? (
                          <div className="text-center py-6">
                            <HelpCircle size={22} className="text-gray-300 mx-auto mb-2" />
                            <p className="text-xs text-gray-500">No Q&A yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {marketFitQa.map((qa) => (
                              <div key={qa.id} className="bg-white rounded p-3 border border-gray-200">
                                {editingQaId === qa.id ? (
                                  <div className="space-y-2">
                                    <textarea
                                      value={editQaQuestion}
                                      onChange={(e) => setEditQaQuestion(e.target.value)}
                                      className="w-full p-2 border border-gray-300 rounded text-xs"
                                      rows={2}
                                      placeholder="Question"
                                    />
                                    <textarea
                                      value={editQaAnswer}
                                      onChange={(e) => setEditQaAnswer(e.target.value)}
                                      className="w-full p-2 border border-gray-300 rounded text-xs"
                                      rows={2}
                                      placeholder="Answer (optional)"
                                    />
                                    <div className="flex gap-1">
                                      <button
                                        type="button"
                                        onClick={() => void handleSaveEditMarketFitQa()}
                                        className="flex-1 px-2 py-1 text-xs bg-satsuma-500 text-white rounded hover:bg-satsuma-600"
                                      >
                                        Save
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setEditingQaId(null)}
                                        className="px-2 py-1 text-xs text-gray-600"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <div className="text-xs font-medium text-gray-800">Q: {qa.question}</div>
                                      {qa.answer ? (
                                        <div className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">A: {qa.answer}</div>
                                      ) : null}
                                      <div className="text-[10px] text-gray-400 mt-1">
                                        {resolveQaAuthorLabel(qa.createdBy, qa.createdByEmail)} ·{' '}
                                        {qa.createdAt ? new Date(qa.createdAt).toLocaleDateString() : ''}
                                      </div>
                                    </div>
                                    <div className="flex items-start gap-1 shrink-0">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditingQaId(qa.id);
                                          setEditQaQuestion(qa.question);
                                          setEditQaAnswer(qa.answer || '');
                                        }}
                                        className="text-gray-400 hover:text-gray-600 transition-colors"
                                      >
                                        <Edit3 size={10} />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => void handleDeleteMarketFitQa(qa.id)}
                                        className="text-gray-400 hover:text-red-600 transition-colors"
                                      >
                                        <Trash2 size={10} />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {isAddingQa && (
                          <div className="mt-3 p-3 bg-white rounded border border-gray-200 space-y-2">
                            <textarea
                              value={newQaQuestion}
                              onChange={(e) => setNewQaQuestion(e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded text-xs"
                              rows={2}
                              placeholder="Question"
                            />
                            <textarea
                              value={newQaAnswer}
                              onChange={(e) => setNewQaAnswer(e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded text-xs"
                              rows={2}
                              placeholder="Answer (optional)"
                            />
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => void handleAddMarketFitQa()}
                                disabled={!newQaQuestion.trim()}
                                className="flex-1 bg-satsuma-500 text-white py-1 px-2 rounded text-xs font-medium hover:bg-satsuma-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Add
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setIsAddingQa(false);
                                  setNewQaQuestion('');
                                  setNewQaAnswer('');
                                }}
                                className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Fixed Footer */}
              <div className="border-t border-gray-200 bg-white/80 backdrop-blur-sm p-6">
                <div className="flex items-center justify-between">
                  {/* Delete Button - Only show for existing products */}
                  {product && onDelete && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="btn-danger disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {isDeleting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Deleting...</span>
                        </>
                      ) : (
                        <>
                          <Trash2 size={16} />
                          <span>Delete Product</span>
                        </>
                      )}
                    </button>
                  )}
                  
                  {/* Save/Cancel Buttons */}
                  <div className="flex items-center space-x-3">
                    {isEditMode ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setIsEditMode(false)}
                          className="btn-secondary"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          form="product-form"
                          className="btn-primary"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <div className="flex items-center space-x-2">
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Saving...</span>
                            </div>
                          ) : (
                            product ? 'Update Product' : 'Create Product'
                          )}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={onClose}
                        className="btn-secondary"
                      >
                        Close
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </>
  );
};

export default ProductModal;
